import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, Overline, MaskedLine } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { SERVICES, PROCESS } from "../lib/site";

const ServicesHero = () => (
  <section data-testid="services-hero" className="pt-48 pb-24 md:pb-32 relative overflow-hidden">
    <div className="aurora aurora-float w-[22rem] h-[22rem] -top-16 -left-20 opacity-25 -z-10" style={{ background: "#FF8A00" }} aria-hidden="true" />
    <div className="aurora aurora-float w-[20rem] h-[20rem] bottom-0 right-0 opacity-20 -z-10" style={{ background: "#7C3AED", animationDelay: "6s" }} aria-hidden="true" />
    <div className="absolute -right-32 top-32 w-[26rem] h-[26rem] rounded-full border border-cyan/15 orbit-ring -z-10 hidden lg:block" aria-hidden="true">
      <span className="absolute top-1/2 -left-1.5 w-3 h-3 rounded-full bg-cyan drop-shadow-[0_0_12px_rgba(0,150,199,0.6)]" />
    </div>
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <MaskedLine delay={0.15}>
        <Overline>Services</Overline>
      </MaskedLine>
      <h1 className="mt-8 font-display font-black text-[11vw] md:text-[6.8vw] leading-[0.92] tracking-tighter" data-testid="services-heading">
        <MaskedLine delay={0.3}>EVERYTHING YOU NEED</MaskedLine>
        <MaskedLine delay={0.45} innerClassName="brand-gradient-text">TO SHIP SOFTWARE.</MaskedLine>
      </h1>
      <MaskedLine delay={0.65} className="max-w-xl mt-10">
        <p className="text-base md:text-lg text-slate-600 leading-relaxed">
          From the first sketch to the millionth user — one team, accountable for the whole journey.
        </p>
      </MaskedLine>
    </div>
  </section>
);

const ServiceCard = ({ s, i }) => (
  <div
    data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    style={{ "--c": s.color, backgroundColor: `${s.color}0D`, borderColor: `${s.color}30` }}
    className="group h-full flex flex-col border rounded-2xl p-8 md:p-10 transition-[border-color,transform,box-shadow] duration-500 hover:border-[color:var(--c)] hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_var(--c)]"
  >
    <div className="flex items-start justify-between">
      <span
        className="w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6"
        style={{ background: `linear-gradient(135deg, ${s.color}, ${s.color}B3)` }}
      >
        <s.icon className="w-7 h-7 text-white" strokeWidth={1.75} />
      </span>
      <span className="font-mono text-xs font-medium" style={{ color: s.color }}>0{i + 1}</span>
    </div>
    <h2 className="mt-8 font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight break-words">{s.title}</h2>
    <p className="mt-4 text-slate-600 text-base leading-relaxed">{s.description}</p>
    <div className="mt-auto pt-8 flex flex-wrap gap-2">
      {s.tags.map((t) => (
        <span key={t} className="font-mono text-[11px] uppercase tracking-wider text-slate-600 bg-white/70 border rounded-full px-3 py-1.5 transition-colors duration-300 group-hover:border-[color:var(--c)]" style={{ borderColor: `${s.color}26` }}>
          {t}
        </span>
      ))}
    </div>
  </div>
);

const ServicesGrid = () => (
  <section data-testid="services-grid" className="pb-32 md:pb-44">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
      {SERVICES.map((s, i) => (
        <FadeUp
          key={s.title}
          delay={(i % 3) * 0.08}
          className={i % 3 === 0 ? "md:col-span-8" : "md:col-span-4"}
        >
          <ServiceCard s={s} i={i} />
        </FadeUp>
      ))}
    </div>
  </section>
);

const Process = () => (
  <section data-testid="services-process" className="py-32 md:py-44 border-t border-ink/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeUp>
        <Overline>How we work</Overline>
        <h2 className="mt-6 font-display font-bold text-4xl md:text-6xl tracking-tighter leading-[1.05]">
          A process with <span className="text-outline-accent">zero drama.</span>
        </h2>
      </FadeUp>
      <div className="mt-16 space-y-0">
        {PROCESS.map((p, i) => (
          <FadeUp key={p.step} delay={i * 0.06}>
            <div
              data-testid={`process-step-${p.step}`}
              className="group grid grid-cols-12 gap-6 items-baseline border-t border-ink/10 py-10 transition-colors duration-500 hover:bg-ink/[0.04]"
            >
              <span className="col-span-2 md:col-span-1 font-mono text-sm font-medium" style={{ color: p.color }}>{p.step}</span>
              <h3 className="col-span-10 md:col-span-4 font-display font-bold text-2xl md:text-4xl tracking-tight transition-transform duration-500 group-hover:translate-x-2">
                {p.title}
              </h3>
              <p className="col-span-10 col-start-3 md:col-span-6 md:col-start-7 text-slate-600 text-base leading-relaxed">
                {p.text}
              </p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2} className="mt-16">
        <Link
          to="/contact"
          data-testid="services-cta-button"
          className="group inline-flex items-center gap-2 text-white font-semibold rounded-full pl-7 pr-6 py-4 bg-[linear-gradient(100deg,#FF8A00,#E0146B,#0096C7)] bg-[length:200%_100%] bg-left transition-[background-position] duration-500 hover:bg-right"
        >
          Tell us about your project
          <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function ServicesPage() {
  return (
    <>
      <ServicesHero />
      <ServicesGrid />
      <EditorialMarquee />
      <Process />
    </>
  );
}
